// Sample the CENTER REGION (middle 60% × 60%) of a cover image and return
// its dominant pixel colour. The center region is the part of the artwork
// guaranteed visible on the printed front cover after cover-fit cropping,
// AND it sidesteps any bleed/trim margins that often surround the main
// plate (yellow padding around a pink artwork, for example). Using this
// for spine + back makes the full A3+ spread read as one continuous panel.
//
// Used by both pdfGenerator.ts (the PDF) and CoverSpreadPreview.tsx (the
// editor preview) so what admin sees in the editor matches what ships in
// the PDF.
//
// Returns `null` when the image can't be loaded/decoded (CORS, network,
// non-image format like PDF). The caller should fall back to
// `cover.dominant_color` or a neutral default.

export async function sampleImageBackgroundHex(imageUrl: string): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const W = 200;
          const H = Math.max(1, Math.round(img.height * (W / img.width)));
          canvas.width = W;
          canvas.height = H;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0, W, H);

          // Central 60% × 60% — skips any bleed margin, keeps plenty of
          // pixels for a meaningful histogram.
          const cx0 = Math.max(0, Math.floor(W * 0.2));
          const cy0 = Math.max(0, Math.floor(H * 0.2));
          const cw  = Math.max(1, Math.floor(W * 0.6));
          const ch  = Math.max(1, Math.floor(H * 0.6));
          const data = ctx.getImageData(cx0, cy0, cw, ch).data;

          const buckets = new Map<string, { r: number; g: number; b: number; n: number }>();
          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 128) continue; // transparent
            const key = `${data[i] >> 4}-${data[i + 1] >> 4}-${data[i + 2] >> 4}`;
            const b = buckets.get(key) ?? { r: 0, g: 0, b: 0, n: 0 };
            b.r += data[i]; b.g += data[i + 1]; b.b += data[i + 2]; b.n++;
            buckets.set(key, b);
          }
          if (buckets.size === 0) return resolve(null);
          let winner: { r: number; g: number; b: number; n: number } | null = null;
          for (const b of buckets.values()) {
            if (!winner || b.n > winner.n) winner = b;
          }
          if (!winner) return resolve(null);
          const r = Math.round(winner.r / winner.n);
          const g = Math.round(winner.g / winner.n);
          const b = Math.round(winner.b / winner.n);
          resolve('#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join(''));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = imageUrl;
    } catch {
      resolve(null);
    }
  });
}
